/**
 * Preferências desta máquina (as seções de Preferências das Configurações: Geral, Chat, Dados,
 * Notificações e Áudio e vídeo; docs/spec/settings.md).
 *
 * Ficam no navegador (localStorage), como as do original ficam no computador. A
 * conta e o perfil, que valem em qualquer lugar, ficam no servidor.
 *
 * Toda preferência daqui tem efeito em algum lugar; nada de opção decorativa.
 */
import { DICE_TEXTURE_LIMITS, DiceEffectRulesSchema, deskFileRefId, type DiceEffectRule } from './shared.js';

export type StartPage = 'inicio' | 'salas';
export type ChatSpacing = 'normal' | 'compact';
/** quando avisar mensagem direta nova (cmbIncomingMessageNotification) */
export type MessageNotice = 'always' | 'unfocused' | 'never';
/**
 * em que unidade ver as distâncias da cena: a da cena, metros, pés, ou os dois ("6 m (20 pés)"),
 * convertidos em `scene/units.ts`
 */
export type DistanceUnit = 'scene' | 'm' | 'ft' | 'both';

export type AppSettings = {
  /** Geral */
  startPage: StartPage;
  chatFontSize: number;
  chatSpacing: ChatSpacing;
  showTimestamps: boolean;
  /** aviso no chat quando as barrinhas dos cartões mudam */
  barNotices: boolean;
  show3DDice: boolean;
  /** mostrar os efeitos especiais dos dados (os de quem rola); com movimento reduzido, vem desligado */
  showDiceEffects: boolean;
  /** esconde as mesas marcadas como +18 no Explorar */
  hideAdultRooms: boolean;
  /** o painel "Amigos e chat" no canto (docs/spec/friends.md); desligado, ele some de todas as telas */
  showFriendsChat: boolean;
  /** a régua e o caminho do movimento na cena */
  distanceUnit: DistanceUnit;
  /** Notificações */
  messageNotice: MessageNotice;
  soundMessages: boolean;
  soundMentions: boolean;
  soundRolls: boolean;
  soundKick: boolean;
  /** sons da cena (portas) */
  soundScene: boolean;
  soundPvt: boolean;
  /** pisca o título da janela quando chega algo com ela em segundo plano */
  flashTitle: boolean;
  /** Chat → Formatação das mensagens (como eu leio o chat) */
  tmHeadings: boolean;
  tmHorizontalLines: boolean;
  tmSmileys: boolean;
  tmInitialCaps: boolean;
  /** Áudio da mesa */
  masterVolume: number;
  /** volume pessoal da música e dos efeitos da mesa (multiplica o geral e o da mesa) */
  musicVolume: number;
  sfxVolume: number;
  audioMuted: boolean;
  /** Áudio e vídeo (a chamada da mesa) */
  joinWithMicOn: boolean;
  joinWithCameraOn: boolean;
  micDeviceId: string;
  cameraDeviceId: string;
  /** saída das vozes da chamada (fone ou alto-falante; `setSinkId`). Vazio: a do sistema */
  speakerDeviceId: string;
  /** Dados → Modo sem iluminação (o inverso) */
  dice3DLighting: boolean;
  /** Dados → Aparência: cores dos meus dados 3D, que os outros também veem */
  diceCustom: boolean;
  diceBody: string;
  diceNumber: string;
  /** Dados → Aparência: a textura dos meus dados, uma imagem do Desk Cloud (`rpgdesk-file:<id>`); null é sem textura */
  diceTexture: string | null;
  /** Dados → Aparência → Tamanho da estampa: o zoom da textura (1 é a imagem inteira em cada face) */
  diceTextureScale: number;
  /**
   * Efeitos especiais dos meus dados (Dados → Efeitos especiais), que vão com cada rolagem; null é o
   * padrão do RPG Desk (o d20 que cai no 20 pega fogo), que acompanha as versões novas
   */
  diceEffects: DiceEffectRule[] | null;
};

/** A pessoa pediu menos movimento no sistema: os efeitos dos dados vêm desligados. */
function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

export const DEFAULT_SETTINGS: AppSettings = {
  startPage: 'inicio',
  chatFontSize: 14,
  chatSpacing: 'normal',
  showTimestamps: true,
  barNotices: true,
  show3DDice: true,
  showDiceEffects: !prefersReducedMotion(),
  hideAdultRooms: true,
  showFriendsChat: true,
  distanceUnit: 'scene',
  messageNotice: 'unfocused',
  soundMessages: true,
  soundMentions: true,
  /** como no original (notifications.playDiceSound): ligado */
  soundRolls: true,
  soundKick: true,
  soundScene: true,
  soundPvt: true,
  flashTitle: true,
  tmHeadings: true,
  tmHorizontalLines: true,
  tmSmileys: true,
  tmInitialCaps: true,
  masterVolume: 0.8,
  musicVolume: 0.8,
  sfxVolume: 0.9,
  audioMuted: false,
  joinWithMicOn: true,
  joinWithCameraOn: false,
  micDeviceId: '',
  cameraDeviceId: '',
  speakerDeviceId: '',
  dice3DLighting: true,
  diceCustom: false,
  diceBody: '#9db1ff',
  diceNumber: '#ffe066',
  diceTexture: null,
  diceTextureScale: 1,
  diceEffects: null,
};

const STORAGE_KEY = 'rpgdesk.settings';

function num(value: unknown, fallback: number, min: number, max: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.min(max, Math.max(min, value)) : fallback;
}
const hex = (v: unknown, d: string): string => (typeof v === 'string' && /^#[0-9a-f]{6}$/i.test(v) ? v : d);
function bool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}
function pick<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
}
function str(value: unknown, max = 200): string {
  return typeof value === 'string' ? value.slice(0, max) : '';
}

/** Regras de efeito gravadas; inválidas voltam ao padrão. */
function diceEffects(value: unknown): DiceEffectRule[] | null {
  if (value === null || value === undefined) return null;
  const parsed = DiceEffectRulesSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function loadSettings(): AppSettings {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const d = JSON.parse(raw) as Record<string, unknown>;
    const D = DEFAULT_SETTINGS;
    return {
      startPage: pick(d.startPage, ['inicio', 'salas'] as const, D.startPage),
      chatFontSize: num(d.chatFontSize, D.chatFontSize, 12, 20),
      // a preferência antiga "chat compacto" vira o espaçamento
      chatSpacing: pick(d.chatSpacing, ['normal', 'compact'] as const, d.compactChat === true ? 'compact' : D.chatSpacing),
      showTimestamps: bool(d.showTimestamps, D.showTimestamps),
      barNotices: bool(d.barNotices, D.barNotices),
      show3DDice: bool(d.show3DDice, D.show3DDice),
      showDiceEffects: bool(d.showDiceEffects, D.showDiceEffects),
      hideAdultRooms: bool(d.hideAdultRooms, D.hideAdultRooms),
      showFriendsChat: bool(d.showFriendsChat, D.showFriendsChat),
      distanceUnit: pick(d.distanceUnit, ['scene', 'm', 'ft', 'both'] as const, D.distanceUnit),
      messageNotice: pick(d.messageNotice, ['always', 'unfocused', 'never'] as const, D.messageNotice),
      soundMessages: bool(d.soundMessages, D.soundMessages),
      soundMentions: bool(d.soundMentions, D.soundMentions),
      soundRolls: bool(d.soundRolls, D.soundRolls),
      soundKick: bool(d.soundKick, D.soundKick),
      soundScene: bool(d.soundScene, D.soundScene),
      soundPvt: bool(d.soundPvt, D.soundPvt),
      flashTitle: bool(d.flashTitle, D.flashTitle),
      tmHeadings: bool(d.tmHeadings, D.tmHeadings),
      tmHorizontalLines: bool(d.tmHorizontalLines, D.tmHorizontalLines),
      tmSmileys: bool(d.tmSmileys, D.tmSmileys),
      tmInitialCaps: bool(d.tmInitialCaps, D.tmInitialCaps),
      masterVolume: num(d.masterVolume, D.masterVolume, 0, 1),
      musicVolume: num(d.musicVolume, D.musicVolume, 0, 1),
      sfxVolume: num(d.sfxVolume, D.sfxVolume, 0, 1),
      audioMuted: bool(d.audioMuted, D.audioMuted),
      joinWithMicOn: bool(d.joinWithMicOn, D.joinWithMicOn),
      joinWithCameraOn: bool(d.joinWithCameraOn, D.joinWithCameraOn),
      micDeviceId: str(d.micDeviceId),
      cameraDeviceId: str(d.cameraDeviceId),
      speakerDeviceId: str(d.speakerDeviceId),
      dice3DLighting: bool(d.dice3DLighting, D.dice3DLighting),
      diceCustom: bool(d.diceCustom, D.diceCustom),
      diceBody: hex(d.diceBody, D.diceBody),
      diceNumber: hex(d.diceNumber, D.diceNumber),
      diceTexture: deskFileRefId(d.diceTexture) ? (d.diceTexture as string) : null,
      diceTextureScale: num(d.diceTextureScale, D.diceTextureScale, DICE_TEXTURE_LIMITS.minScale, DICE_TEXTURE_LIMITS.maxScale),
      diceEffects: diceEffects(d.diceEffects),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: AppSettings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // sem localStorage (modo privado): as preferências valem só nesta sessão
  }
}

/** Aplica o que é global na página: o tamanho e o espaçamento do texto do chat. */
export function applyGlobalSettings(settings: AppSettings): void {
  const root = document.documentElement;
  root.style.setProperty('--chat-font-size', `${settings.chatFontSize}px`);
  root.style.setProperty('--chat-line-height', settings.chatSpacing === 'compact' ? '1.25' : '1.5');
}

/** Opções de leitura do Talemark que vêm das preferências de Texto. */
export function talemarkPrefs(settings: AppSettings) {
  return {
    headings: settings.tmHeadings,
    horizontalLines: settings.tmHorizontalLines,
    smileys: settings.tmSmileys,
    initialCaps: settings.tmInitialCaps,
  };
}
